import { type ApiScan, isScanActive } from '@kaiizen/kai-pro-api';

export type ScopeArtifactDocumentType = 'changeOrder' | 'fullScope';
export type HistoricalArtifactDocumentType = ScopeArtifactDocumentType | 'closeout';

type ScopeArtifactDocument = {
  documentType: ScopeArtifactDocumentType;
  kicker: string;
  title: string;
};

/** Shared timestamp sorter for artifact scan collections. */
export const getArtifactTime = (value: Date | string | undefined) => value ? new Date(value).getTime() : 0;

/** Formats artifact dates for card and badge copy. */
export const formatArtifactDate = (value: Date | string | undefined) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    : 'Unknown date';

/** Formats an artifact summary total or the supplied live total for the active working copy. */
export const formatArtifactAmount = (
  scan: Pick<ApiScan, 'totalLaborCost' | 'totalMaterialCost'>,
  liveAmount?: number,
) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(liveAmount ?? (scan.totalLaborCost ?? 0) + (scan.totalMaterialCost ?? 0));

/** Narrowing helper for shareable audience-doc scans. */
export const isAudienceDocScan = (scan: Pick<ApiScan, 'scanType'>) => scan.scanType === 'audienceDoc';

/** Narrowing helper for scope-history artifacts. */
export const isScopeArtifactScan = (
  scan: Pick<ApiScan, 'scanType'>,
) =>
  scan.scanType == null
  || scan.scanType === 'initial'
  || scan.scanType === 'changeRequest'
  || scan.scanType === 'progress';

/** Scope artifacts include approved versions and active working copies, never rejected scans. */
export const isHistoricalScopeArtifactScan = (
  scan: Pick<ApiScan, 'scanLifecycleStatus' | 'scanType'>,
) => isScopeArtifactScan(scan) && (scan.scanLifecycleStatus === 'approved' || isScanActive(scan));

/** Closeout artifacts are immutable snapshots from an approved, completed progress walk. */
export const isCloseoutArtifactScan = (
  scan: Pick<ApiScan, 'scanLifecycleStatus' | 'scanType'>,
) => scan.scanType === 'progressCompleted' && scan.scanLifecycleStatus === 'approved';

/** Historical artifacts include scope versions and the final approved closeout. */
export const isHistoricalArtifactScan = (
  scan: Pick<ApiScan, 'scanLifecycleStatus' | 'scanType'>,
) => isHistoricalScopeArtifactScan(scan) || isCloseoutArtifactScan(scan);

/** Human-readable title for a scope-history artifact card. */
export const getHistoricalArtifactTitle = (
  scan: Pick<ApiScan, 'id' | 'scanType'>,
  changeOrderNumberById: Map<string, number>,
) => {
  if (scan.scanType !== 'changeRequest') return 'Scope';
  return `Change Order ${changeOrderNumberById.get(scan.id) ?? 1}`;
};

/** Documents available for an approved or active scope version. */
export const getScopeArtifactDocuments = (
  scan: Pick<ApiScan, 'id' | 'scanLifecycleStatus' | 'scanType'>,
  changeOrderNumberById: Map<string, number>,
): ScopeArtifactDocument[] => {
  const changeOrderNumber = changeOrderNumberById.get(scan.id) ?? 1;
  if (scan.scanLifecycleStatus !== 'approved') {
    return [{
      documentType: 'fullScope',
      kicker: scan.scanType === 'changeRequest' ? `Draft change order ${changeOrderNumber}` : 'Draft scope',
      title: 'Draft Scope',
    }];
  }

  if (scan.scanType !== 'changeRequest') {
    return [{ documentType: 'fullScope', kicker: 'Original scope', title: 'Scope' }];
  }

  return [
    {
      documentType: 'changeOrder',
      kicker: `Change order ${changeOrderNumber}`,
      title: `Change Order ${changeOrderNumber}`,
    },
    {
      documentType: 'fullScope',
      kicker: `Change order ${changeOrderNumber}`,
      title: 'Full Scope',
    },
  ];
};

/** Resolves a linked artifact document, preserving the change-order document as the default. */
export const getScopeArtifactDocumentType = (
  scan: Pick<ApiScan, 'scanLifecycleStatus' | 'scanType'>,
  requestedDocumentType?: string | null,
): ScopeArtifactDocumentType =>
  scan.scanLifecycleStatus === 'approved'
    && scan.scanType === 'changeRequest'
    && requestedDocumentType !== 'fullScope'
    ? 'changeOrder'
    : 'fullScope';

/** Resolves the document type for every card in the historical artifact grid. */
export const getHistoricalArtifactDocumentType = (
  scan: Pick<ApiScan, 'scanLifecycleStatus' | 'scanType'>,
  requestedDocumentType?: string | null,
): HistoricalArtifactDocumentType =>
  isCloseoutArtifactScan(scan)
    ? 'closeout'
    : getScopeArtifactDocumentType(scan, requestedDocumentType);

/** Status badge text for a scope-history artifact card. */
export const getHistoricalArtifactBadge = ({
  scan,
  latestApprovedArtifactId,
}: {
  scan: Pick<ApiScan, 'id' | 'scanLifecycleStatus' | 'scanType'>;
  latestApprovedArtifactId?: string;
}) => {
  if (scan.scanLifecycleStatus !== 'approved') return 'In Draft';
  if (scan.scanType !== 'changeRequest') return 'Original';
  if (scan.id === latestApprovedArtifactId) return 'Latest';
  return 'Outdated';
};

/** Visual tone for a historical artifact badge. */
export const getHistoricalArtifactBadgeTone = (badge: string) => {
  if (badge === 'Latest') return 'accent' as const;
  if (badge === 'In Draft') return 'neutral' as const;
  return 'neutral' as const;
};

/** Secondary metadata copy for a historical artifact card. */
export const getHistoricalArtifactSubtitle = (
  scan: Pick<ApiScan, 'createdAt' | 'approvedAt' | 'scanLifecycleStatus'>,
) =>
  scan.scanLifecycleStatus === 'approved'
    ? `Approved ${formatArtifactDate(scan.approvedAt ?? scan.createdAt)}`
    : `Working copy · started ${formatArtifactDate(scan.createdAt)}`;

/** Default name when minting a new audience-doc copy from a scope version. */
export const getDefaultAudienceDocName = (scan?: Pick<ApiScan, 'name'>) => scan?.name ? `Audience Doc - ${scan.name}` : 'Audience Doc';
